import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import type { LLMGroupResponseType } from 'state';
import TopBar from './TopBar';
import Rail, { RailSortKey } from './Rail';
import DetailView, { DetailTab, StudioMode } from './DetailView';
import LeaderboardDrawer from './LeaderboardDrawer';
import type { ScatterPoint } from './RiskReturnScatter';
import type { Studio2Player, Studio2Session } from '../live/types';
import StudioSkeleton from './StudioSkeleton';
import type { RelativizeDate } from '../helpers';

/** Sub-tab order for ←/→ keyboard navigation. */
const TAB_ORDER: DetailTab[] = [
  'snapshot',
  'drivers',
  'risk',
  'behavior',
  'bias',
  'decisions',
  'report',
];

// Stable references so DetailView's props and the keyboard effect don't churn.
const BIAS_REPORT_LOCKED: DetailTab[] = ['bias', 'report'];
const NO_LOCKED_TABS: DetailTab[] = [];

/** Measure distance from viewport top and fill the rest — guarantees the page
 *  frame never scrolls regardless of header/banner height. */
const useFillHeight = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState<number | undefined>(undefined);
  useLayoutEffect(() => {
    const measure = () => {
      if (!ref.current) return;
      const top = ref.current.getBoundingClientRect().top;
      setHeight(Math.max(440, window.innerHeight - top - 10));
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);
  return { ref, height };
};

const sortPlayers = (players: Studio2Player[], key: RailSortKey) => {
  const arr = [...players];
  switch (key) {
    case 'return':
      return arr.sort(
        (a, b) => b.last_portfolio_return - a.last_portfolio_return,
      );
    case 'sharpe':
      return arr.sort(
        (a, b) => b.last_metrics.sharpe_ratio - a.last_metrics.sharpe_ratio,
      );
    case 'mktval':
      return arr.sort((a, b) => b.last_market_value - a.last_market_value);
    case 'name':
      return arr.sort((a, b) => a.name.localeCompare(b.name));
    case 'rank':
    default:
      return arr.sort((a, b) => a.report.returns.rank - b.report.returns.rank);
  }
};

type Props = {
  mode: StudioMode;
  players: Studio2Player[];
  session: Studio2Session;
  groupReports: LLMGroupResponseType[];
  // False while the real session + roster are still loading — the shell shows a
  // full-page skeleton instead of any (never-mock) placeholder data.
  isReady: boolean;
  // Self view only — anonymised cohort scatter points.
  scatterPoints?: ScatterPoint[];
  // Self + historical only — relativises scenario dates to "Day N". Absent in
  // the trainer/cohort view, where real dates are kept.
  relativizeDate?: RelativizeDate;
  // Selection lives in the parent root because the cohort data hook is
  // parameterised by the focused player (its report + blotter are enriched only
  // for that player). The shell drives selection via onSelect; in the self view
  // it is pinned to the trainee's own id and onSelect is a no-op. `null` before
  // the first real roster arrives (cohort) — the shell snaps to a real player.
  selectedId: number | null;
  onSelect: (id: number) => void;
};

/** Presentational studio shell shared by the trainer (cohort) and trainee (self)
 *  views. Owns the layout, the secondary UI state (search / sort / active-only /
 *  rail-collapse / sub-tab) and the keyboard navigation. The cohort-only chrome
 *  (Rail roster, Leaderboard drawer, online count, ↑/↓ player nav) is gated on
 *  `mode === 'cohort'`. */
const StudioShell: React.FC<Props> = ({
  mode,
  players,
  session,
  groupReports,
  isReady,
  scatterPoints,
  relativizeDate,
  selectedId,
  onSelect,
}) => {
  const { ref, height } = useFillHeight();
  const isCohort = mode === 'cohort';

  // Self (trainee) view only: gate the Bias & Final-report tabs behind the
  // session flag. The trainer/cohort view never locks them.
  const biasReportLocked =
    mode === 'self' && !session.show_bias_and_report_in_player_view;
  const lockedTabs = biasReportLocked ? BIAS_REPORT_LOCKED : NO_LOCKED_TABS;

  const [search, setSearch] = useState('');
  // "Active only" defaults ON when the roster has at least one active (traded,
  // deals !== 0) account, OFF when none — so a session where nobody has traded
  // yet still shows the full roster. Seeded here for the case where the real
  // roster is already present at mount.
  const [showActiveOnly, setShowActiveOnly] = useState(
    () => isCohort && isReady && players.some(p => p.deals !== 0),
  );
  const [railCollapsed, setRailCollapsed] = useState(false);
  const [sortKey, setSortKey] = useState<RailSortKey>('return');
  const [detailTab, setDetailTab] = useState<DetailTab>('snapshot');

  // Decide the "Active only" default once, off the REAL roster — while the
  // session loads the roster is empty (isReady false), so wait for real data to
  // arrive. Latched afterwards so it never fights a manual toggle or live roster
  // updates.
  const activeOnlyDecided = useRef(isCohort && isReady && players.length > 0);
  useEffect(() => {
    if (activeOnlyDecided.current) return;
    if (!isCohort || !isReady || !players.length) return;
    activeOnlyDecided.current = true;
    setShowActiveOnly(players.some(p => p.deals !== 0));
  }, [isCohort, isReady, players]);

  const visiblePlayers = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = players.filter(p => {
      // "Active" = the account has traded — matches the Overview players table's
      // "Only show active accounts" filter (player.deals !== 0). This is trading
      // activity, NOT live websocket presence (p.online).
      if (showActiveOnly && p.deals === 0) return false;
      if (q && !p.name.toLowerCase().includes(q)) return false;
      return true;
    });
    return sortPlayers(filtered, sortKey);
  }, [players, search, showActiveOnly, sortKey]);

  // Resolves to a real player (context preserved even if the selected player is
  // filtered out of the rail). `undefined` only while the roster is empty
  // (loading) — the render is gated on `isReady` so the real UI never mounts
  // with an undefined player.
  const detailPlayer: Studio2Player | undefined =
    players.find(p => p.id === selectedId) ?? visiblePlayers[0] ?? players[0];

  const onlineCount = players.filter(p => p.online).length;

  // Keep the selection valid when the cohort changes (e.g. the mock seed id
  // doesn't exist in the real roster) — snap to the first available player.
  // No-op in the self view (the single player id is always valid).
  useEffect(() => {
    if (!isCohort) return;
    if (players.length && !players.some(p => p.id === selectedId)) {
      onSelect(visiblePlayers[0]?.id ?? players[0].id);
    }
  }, [isCohort, players, visiblePlayers, selectedId, onSelect]);

  // If the tab the player is on becomes locked (admin toggles the flag off
  // live), move them to Snapshot rather than leaving them on a locked tab.
  useEffect(() => {
    if (lockedTabs.includes(detailTab)) setDetailTab('snapshot');
  }, [lockedTabs, detailTab]);

  // Global keyboard navigation for the whole studio page:
  //  ↑/↓  → previous / next player in the roster (cohort view only)
  //  ←/→  → previous / next detail sub-tab
  // ↑/↓ are reserved exclusively for player navigation — even right after you
  // click a Segmented / sub-tab (which exposes a radio <input> that would
  // otherwise steal the arrows). ←/→ still defer to a focused Segmented; both
  // ignore genuine text inputs (search box, open dropdowns).
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const el = e.target as HTMLElement | null;
      const tag = el?.tagName;
      const inputType = tag === 'INPUT' ? (el as HTMLInputElement).type : '';
      // Only real text entry should swallow the keys. A Segmented's radio input
      // and the "Active only" switch (a button) must NOT count.
      const inTextField =
        tag === 'TEXTAREA' ||
        !!el?.isContentEditable ||
        !!el?.closest?.('.ant-select') ||
        (tag === 'INPUT' &&
          !['radio', 'checkbox', 'button', 'submit', 'reset', 'range'].includes(
            inputType,
          ));

      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        // Player navigation is a cohort-only affordance.
        if (!isCohort || inTextField || !visiblePlayers.length) return;
        // Stop here in the capture phase so a focused Segmented/tab never sees it.
        e.preventDefault();
        e.stopPropagation();
        const idx = visiblePlayers.findIndex(p => p.id === detailPlayer?.id);
        let next: number;
        if (idx < 0) next = 0;
        else if (e.key === 'ArrowDown')
          next = Math.min(visiblePlayers.length - 1, idx + 1);
        else next = Math.max(0, idx - 1);
        onSelect(visiblePlayers[next].id);
      } else if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
        if (inTextField) return;
        if (el?.closest?.('.ant-segmented')) return;
        e.preventDefault();
        // Skip locked tabs (self view with bias/report gated) and the Risk tab
        // (no benchmark → hidden in DetailView) so the arrows never land on a
        // disabled/hidden tab.
        const order = TAB_ORDER.filter(
          t =>
            !(biasReportLocked && (t === 'bias' || t === 'report')) &&
            !(!session.has_benchmark && t === 'risk'),
        );
        const i = order.indexOf(detailTab);
        const ni =
          e.key === 'ArrowRight'
            ? Math.min(order.length - 1, i + 1)
            : Math.max(0, i - 1);
        setDetailTab(order[ni] ?? detailTab);
      }
    };
    // Capture phase so ↑/↓ are intercepted before a focused control reacts.
    window.addEventListener('keydown', handler, true);
    return () => window.removeEventListener('keydown', handler, true);
  }, [
    isCohort,
    biasReportLocked,
    session.has_benchmark,
    visiblePlayers,
    detailPlayer?.id,
    detailTab,
  ]);

  // The `.studio2` container (with the measured fill-height) stays mounted in
  // both states, so swapping the skeleton for the real content on load never
  // shifts the frame. Gating on `detailPlayer` too narrows it to a real player
  // for the render — it is only undefined while the roster is empty (loading).
  return (
    <div ref={ref} className="studio2" style={{ height }}>
      {isReady && detailPlayer ? (
        <>
          <TopBar
            session={session}
            onlineCount={isCohort ? onlineCount : undefined}
            totalCount={isCohort ? players.length : undefined}
          />

          <div className="studio2__body">
            {isCohort && <LeaderboardDrawer />}

            {isCohort && (
              <Rail
                players={visiblePlayers}
                selectedId={detailPlayer.id}
                onSelect={onSelect}
                collapsed={railCollapsed}
                onToggleCollapsed={() => setRailCollapsed(c => !c)}
                search={search}
                onSearch={setSearch}
                sortKey={sortKey}
                onSortKey={setSortKey}
                showActiveOnly={showActiveOnly}
                onToggleActive={setShowActiveOnly}
              />
            )}

            <div className="studio2__workspace">
              <DetailView
                player={detailPlayer}
                cohort={players}
                groupReports={groupReports}
                session={session}
                tab={detailTab}
                onTab={setDetailTab}
                mode={mode}
                scatterPoints={scatterPoints}
                relativizeDate={relativizeDate}
                lockedTabs={lockedTabs}
              />
            </div>
          </div>
        </>
      ) : (
        <StudioSkeleton mode={mode} />
      )}
    </div>
  );
};

export default StudioShell;
